import React, { useMemo } from 'react';
import { StyleSheet, Text as RNText } from 'react-native';
import type { TextProps, TextStyle, StyleProp } from 'react-native';
import { useAppStore } from '@restorant/core';
import { fontStyle, scaleFont, useColors, useTextScale } from '../theme';
import type { FontWeight } from '../theme';
import { useFontsReady } from '../fonts';

export interface TProps extends TextProps {
  size?: number;
  weight?: FontWeight;
  serif?: boolean;
  color?: string;
  style?: StyleProp<TextStyle>;
}

/**
 * The one place fonts are resolved: family, weight, and the reading-size scale.
 * Screens use <T> instead of RN's <Text> so all three live here and nowhere else.
 */
export function T({
  size = 14,
  weight = 400,
  serif = false,
  color,
  style,
  ...rest
}: TProps) {
  const colors = useColors();
  const fontsReady = useFontsReady();
  const scale = useTextScale();
  const lang = useAppStore((s) => s.lang);

  // Manrope and Fraunces carry no Devanagari or Gujarati glyphs, so those
  // languages fall back to the platform font rather than rendering tofu.
  const latin = lang === 'en';

  // An explicit lineHeight would clip once the text grows, so scale it too.
  const scaledStyle = useMemo(() => {
    const flat = StyleSheet.flatten(style) as TextStyle | undefined;
    if (!flat || typeof flat.lineHeight !== 'number') return style;
    return [style, { lineHeight: flat.lineHeight * scale }];
  }, [style, scale]);

  return (
    <RNText
      {...rest}
      style={[
        { fontSize: scaleFont(size, scale), color: color ?? colors.text },
        fontStyle(weight, serif, fontsReady && latin),
        scaledStyle,
      ]}
    />
  );
}
